import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaClock,
  FaCar,
  FaFileInvoice,
  FaHistory,
  FaTimes,
  FaUser,
  FaEnvelope,
  FaPhoneAlt,
  FaKey,
  FaStar,
  FaCut,
  FaThumbsUp,
  FaThumbsDown
} from "react-icons/fa";
import api from "../api/Api";
import Loader from "../components/history_loader";

// --- BULLETPROOF TIME FORMATTER ---
const formatDateTime = (dateStr, timeStr) => {
  if (!dateStr) return "—";

  const dateObj = new Date(dateStr);
  const formattedDate = dateObj.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

  let formattedTime = "";
  if (timeStr) {
    const timeString = String(timeStr);
    if (timeString.includes("T")) {
      const timeObj = new Date(timeString);
      formattedTime = timeObj.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } else {
      const cleanTime = timeString
        .padStart(4, "0")
        .replace(/(\d{2})(\d{2})/, "$1:$2");
      const [hours, minutes] = cleanTime.split(":");
      if (hours !== undefined && minutes !== undefined) {
        const tempDate = new Date();
        tempDate.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
        formattedTime = tempDate.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });
      }
    }
  }

  return formattedTime ? `${formattedDate} · ${formattedTime}` : formattedDate;
};

export default function MyBookings() {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("upcoming");
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  // --- NATIVE PURPLE CARD STYLES ---
  const nativeStyles = {
    tripCard: {
      background: "linear-gradient(135deg, #fa6400 0%, #525dee 100%)",
      borderRadius: "20px",
      border: "none",
      boxShadow: "0 8px 24px rgba(109, 40, 217, 0.25)",
      color: "white",
      overflow: "hidden",
    },
    glassBox: {
      background: "rgba(255, 255, 255, 0.1)",
      border: "1px solid rgba(255, 255, 255, 0.05)",
      borderRadius: "16px",
    },
  };

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("customerToken");
      const response = await api.get("/bookingRoutes/customerTrips", {
        headers: { Authorization: `Bearer ${token}` },
      });
      // console.log("Raw API Response:", response.data);

      const mappedData = response.data.map((trip) => {
        let statusStr = "Pending";
        if (trip.enq_status === 1) statusStr = "Confirmed";
        if (trip.enq_status === 2) statusStr = "Expired";
        if (trip.enq_status === 3) statusStr = "Revised";

        // ---  1: STRICT OVERRIDE HIERARCHY ---
        let actualStatus = statusStr;
        if (trip.is_cancelled === 1) {
          actualStatus = "Cancelled";
        } else if (Number(trip.cancel_request) === 1) {
          actualStatus = "CancelPending";
        } else if (trip.isTrip_end === 1) {
          actualStatus = "Completed";
        }

        let rideDateTime = new Date();
        if (trip.Journey_date || trip.arrive_dt) {
          rideDateTime = new Date(trip.Journey_date || trip.arrive_dt);
          if (trip.picktime_id) {
            const timeString = String(trip.picktime_id);
            if (timeString.includes("T")) {
              const tObj = new Date(timeString);
              rideDateTime.setHours(tObj.getHours(), tObj.getMinutes(), 0, 0);
            } else {
              const cleanT = timeString
                .padStart(4, "0")
                .replace(/(\d{2})(\d{2})/, "$1:$2");
              const [h, m] = cleanT.split(":");
              if (h !== undefined && m !== undefined) {
                rideDateTime.setHours(parseInt(h, 10), parseInt(m, 10), 0, 0);
              }
            }
          }
        }

        return {
          id: trip.enq_id || trip.enqref_no,
          referenceNo: trip.reference_no,
          enqRefNo: trip.enqref_no,
          Journey_date: trip.Journey_date,
          enquiryDate: trip.enquiry_date || trip.enq_createddate,
          confirmationDate: trip.confirmation_date || trip.conf_date,
          rideDateTime: trip.exact_ride_datetime
            ? new Date(trip.exact_ride_datetime)
            : rideDateTime,
          isCancelled: trip.is_cancelled === 1,
          status: actualStatus,
          carName: trip.taxi_car_name || "Assigned Cab",
          date: new Date(
            trip.arrive_dt || trip.Journey_date,
          ).toLocaleDateString("en-GB", {
            day: "numeric",
            month: "short",
            year: "numeric",
          }),
          time:
            trip.exact_time_string ||
            trip.Pickup_time ||
            trip.arrive_time ||
            "00:00:00",
          pickup: trip.pickup_location || "Not Specified",
          drop: trip.dropoff_location || "Not Specified",
          price: Number(trip.final_price) || 0,
          customerName: trip.gustcust_name || "Guest",
          customerEmail: trip.guestemail_id || "N/A",
          customerPhone: trip.guestmob_no || "N/A",
          driver: trip.driver_name || null,
          driverId: trip.driver_id || null,
          driverPhone: trip.driver_phone || null,
          vehicleNo: trip.vehicle_no || null,
          // trip Feedback
          feedbackRating: trip.feedback_rating || null,
          feedbackRecommend: trip.feedback_recommend,
        };
      });

      setBookings(mappedData);
    } catch (err) {
      console.error("Error fetching bookings:", err);
      setError(err.response?.data?.message || "Failed to load your trips.");
    } finally {
      setLoading(false);
    }
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case "Confirmed":
        return { backgroundColor: "rgba(255,255,255,0.95)", color: "#6d28d9" };
      case "Expired":
        return { backgroundColor: "rgba(255,255,255,0.2)", color: "#cccccc" };
      case "Revised":
        return { backgroundColor: "#fef3c7", color: "#92400e" };
      case "Cancelled":
        return { backgroundColor: "#fee2e2", color: "#991b1b" };
      case "CancelPending":
        return {
          backgroundColor: "#fef2f2",
          color: "#dc2626",
          border: "1px solid #fecaca",
        };
      default:
        return { backgroundColor: "rgba(255,255,255,0.2)", color: "#ffffff" };
    }
  };

  // ---  2: UPDATE STATUS LABEL ---
  const getStatusLabel = (status) => {
    if (status === "CancelPending") return "Cancel on Review";
    return status;
  };

  const handleCancelClick = (e, booking) => {
    e.stopPropagation();
    setSelectedBooking(booking);
    setShowCancelModal(true);
  };

  const confirmCancellation = async () => {
    setCancelling(true);
    try {
      const token = localStorage.getItem("customerToken");
      const response = await api.post(
        "/bookingRoutes/cancelEnquiry",
        { enq_id: selectedBooking.id, reason: "Requested by customer" },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.data.success) {
        setBookings((prev) =>
          prev.map((b) =>
            b.id === selectedBooking.id ? { ...b, status: "CancelPending" } : b,
          ),
        );
      }
    } catch (err) {
      alert("Could not cancel booking. Please try again.");
    } finally {
      setCancelling(false);
      setShowCancelModal(false);
      setSelectedBooking(null);
    }
  };

  const filteredBookings = bookings.filter((b) => {
    const now = new Date();
    const isFutureRide = b.rideDateTime >= now;

    if (activeTab === "upcoming") {
      return (
        (b.status === "Confirmed" ||
          b.status === "Pending" ||
          b.status === "CancelPending") &&
        isFutureRide
      );
    }

    return (
      b.status === "Completed" ||
      b.status === "Expired" ||
      b.status === "Revised" ||
      b.status === "Cancelled" ||
      !isFutureRide
    );
  });

  return (
    <div className="d-flex flex-column min-vh-100 bg-white font-sans overflow-hidden">
      <div
        className="container-fluid flex-grow-1 p-0 pb-4"
        style={{ maxWidth: "800px", margin: "0 auto" }}
      >
        {/* --- 1. SEGMENTED TABS --- */}
        <div
          className="bg-white px-3 py-3 shadow-sm mb-3 position-relative z-index-1"
          style={{ borderBottom: "1px solid #f1f5f9" }}
        >
          <div
            className="p-1 rounded-pill d-flex w-100"
            style={{ backgroundColor: "#f1f5f9", border: "1px solid #e2e8f0" }}
          >
            <button
              className={`btn flex-fill rounded-pill py-2 fw-bold transition-all btn-native-press ${activeTab === "upcoming" ? "shadow-sm border-0" : "border-0 bg-transparent"}`}
              style={{
                fontSize: "0.85rem",
                background:
                  activeTab === "upcoming"
                    ? "linear-gradient(135deg, #f97316 0%, #ea580c 100%)"
                    : "transparent",
                color: activeTab === "upcoming" ? "#ffffff" : "#64748b",
              }}
              onClick={() => setActiveTab("upcoming")}
            >
              Upcoming
            </button>
            <button
              className={`btn flex-fill rounded-pill py-2 fw-bold transition-all btn-native-press ${activeTab === "history" ? "shadow-sm border-0" : "border-0 bg-transparent"}`}
              style={{
                fontSize: "0.85rem",
                background:
                  activeTab === "history"
                    ? "linear-gradient(135deg, #f97316 0%, #ea580c 100%)"
                    : "transparent",
                color: activeTab === "history" ? "#ffffff" : "#64748b",
              }}
              onClick={() => setActiveTab("history")}
            >
              Past Rides
            </button>
          </div>
        </div>

        {/* --- 2. MAIN CONTENT AREA --- */}
        {loading ? (
          <div className="d-flex flex-column justify-content-center align-items-center w-100 mt-5 pt-5">
            <Loader />
          </div>
        ) : error ? (
          <div
            className="text-center py-5 mt-4 mx-3 bg-white rounded-4 shadow-sm"
            style={{ border: "1px solid #fef2f2" }}
          >
            <div className="bg-danger bg-opacity-10 text-danger rounded-circle d-inline-flex p-3 mb-3">
              <FaTimes size={24} />
            </div>
            <h6 className="fw-bold text-dark mb-2">
              Oops! Something went wrong
            </h6>
            <p className="text-muted small mb-4 px-3">{error}</p>
            <button
              className="btn rounded-pill px-4 fw-bold btn-native-press"
              style={{
                backgroundColor: "#f97316",
                color: "#ffffff",
                border: "none",
              }}
              onClick={fetchBookings}
            >
              Try Again
            </button>
          </div>
        ) : (
          <div className="px-3">
            {filteredBookings.length > 0 ? (
              filteredBookings.map((item, index) => (
                <React.Fragment key={item.id}>
                  <div
                    className="card border-0 animate-slide-up bg-white"
                    style={{
                      borderRadius: "24px",
                      boxShadow: "0 10px 40px rgba(234, 88, 12, 0.08)",
                      border: "1px solid rgba(234, 88, 12, 0.1)",
                      overflow: "hidden",
                      animationDelay: `${index * 0.05}s`,
                    }}
                  >
                    {/* --- 1. CARD HEADER --- */}
                    <div
                      className="p-3 pb-2 d-flex justify-content-between align-items-start border-bottom"
                      style={{ borderColor: "#f8fafc" }}
                    >
                      <div className="d-flex flex-column pe-2">
                        <span
                          className="fw-bold mb-1"
                          style={{
                            fontSize: "0.65rem",
                            textTransform: "uppercase",
                            letterSpacing: "1px",
                            color: "#f97316",
                          }}
                        >
                          Booking ID
                        </span>
                        <span
                          className="fw-bold text-dark mb-1"
                          style={{
                            fontSize: "1.1rem",
                            letterSpacing: "-0.5px",
                          }}
                        >
                          {item.referenceNo || item.enqRefNo}
                        </span>

                        {/* Date Logic */}
                        {item.status === "Confirmed" &&
                        item.confirmationDate ? (
                          <span
                            className="fw-bold text-success"
                            style={{ fontSize: "0.75rem" }}
                          >
                            Confirmed:{" "}
                            {new Date(item.confirmationDate).toLocaleString(
                              "en-IN",
                              {
                                day: "numeric",
                                month: "short",
                                hour: "2-digit",
                                minute: "2-digit",
                                hour12: true,
                              },
                            )}
                          </span>
                        ) : item.enquiryDate ? (
                          <span
                            className="fw-medium text-muted"
                            style={{ fontSize: "0.75rem" }}
                          >
                            Booked:{" "}
                            {new Date(item.enquiryDate).toLocaleString(
                              "en-IN",
                              {
                                day: "numeric",
                                month: "short",
                                hour: "2-digit",
                                minute: "2-digit",
                                hour12: true,
                              },
                            )}
                          </span>
                        ) : null}
                      </div>

                      <div className="d-flex flex-column align-items-end flex-shrink-0">
                        <span
                          className="text-dark fw-bold mb-2 text-end"
                          style={{ fontSize: "0.85rem" }}
                        >
                          {formatDateTime(item.Journey_date, item.time)}
                        </span>
                        {/* Pastel Status Badges */}
                        <span
                          className="badge rounded-pill px-3 py-2 fw-bold d-flex align-items-center gap-2 shadow-sm"
                          style={{
                            ...getStatusStyle(item.status),
                            fontSize: "0.75rem",
                          }}
                        >
                          {item.status === "Confirmed" && (
                            <div
                              style={{
                                width: 6,
                                height: 6,
                                borderRadius: "50%",
                                background: "#16a34a",
                              }}
                            />
                          )}
                          {getStatusLabel(item.status)}
                        </span>
                      </div>
                    </div>

                    {/* --- 2. PASSENGER BOX (Soft Orange Gradient Inset) --- */}
                    <div className="px-3 pt-3 pb-2">
                      <div
                        className="d-flex align-items-center gap-3 p-3 rounded-4"
                        style={{
                          background:
                            "linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)",
                          border: "1px solid #fed7aa",
                        }}
                      >
                        <div
                          className="bg-white rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 shadow-sm"
                          style={{
                            width: "42px",
                            height: "42px",
                            color: "#ea580c",
                          }}
                        >
                          <FaUser size={16} />
                        </div>
                        <div className="flex-grow-1 overflow-hidden">
                          <div
                            className="fw-bold mb-1"
                            style={{
                              fontSize: "0.6rem",
                              letterSpacing: "1px",
                              textTransform: "uppercase",
                              color: "#ea580c",
                            }}
                          >
                            PASSENGER
                          </div>
                          <div
                            className="fw-bold text-dark text-truncate mb-1"
                            style={{ fontSize: "0.95rem" }}
                          >
                            {item.customerName}
                          </div>
                          <div className="d-flex align-items-center gap-3">
                            <span
                              className="fw-semibold d-flex align-items-center gap-1 text-muted"
                              style={{ fontSize: "0.75rem" }}
                            >
                              <FaPhoneAlt
                                size={10}
                                style={{ color: "#ea580c" }}
                              />{" "}
                              {item.customerPhone}
                            </span>
                            <span
                              className="fw-semibold d-flex align-items-center gap-1 text-truncate text-muted"
                              style={{ fontSize: "0.75rem" }}
                            >
                              <FaEnvelope
                                size={10}
                                style={{ color: "#ea580c" }}
                              />{" "}
                              {item.customerEmail}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* --- 3. ROUTE TIMELINE --- */}
                    <div className="px-3 position-relative mt-2 mb-3">
                      {item.drop !== "Not Specified" && (
                        <div
                          className="position-absolute"
                          style={{
                            left: "21px",
                            top: "16px",
                            bottom: "16px",
                            borderLeft: "2px dashed #cbd5e1",
                            zIndex: 0,
                          }}
                        />
                      )}

                      {/* Pickup */}
                      <div className="d-flex position-relative mb-3">
                        <div
                          className="mt-1 me-3 bg-white z-index-1 flex-shrink-0 shadow-sm"
                          style={{
                            width: "14px",
                            height: "14px",
                            borderRadius: "50%",
                            border: "3px solid #22c55e",
                          }}
                        />
                        <div className="flex-grow-1">
                          <div
                            className="fw-bold text-muted mb-1"
                            style={{
                              fontSize: "0.6rem",
                              letterSpacing: "1px",
                              textTransform: "uppercase",
                            }}
                          >
                            PICKUP
                          </div>
                          <div
                            className="fw-bold text-dark text-truncate"
                            style={{ fontSize: "0.9rem", maxWidth: "280px" }}
                          >
                            {item.pickup}
                          </div>
                        </div>
                      </div>

                      {/* Drop-off */}
                      {item.drop !== "Not Specified" && (
                        <div className="d-flex position-relative">
                          <div
                            className="mt-1 me-3 bg-white z-index-1 flex-shrink-0 shadow-sm"
                            style={{
                              width: "14px",
                              height: "14px",
                              borderRadius: "2px",
                              border: "3px solid #ef4444",
                            }}
                          />
                          <div className="flex-grow-1">
                            <div
                              className="fw-bold text-muted mb-1"
                              style={{
                                fontSize: "0.6rem",
                                letterSpacing: "1px",
                                textTransform: "uppercase",
                              }}
                            >
                              DROP-OFF
                            </div>
                            <div
                              className="fw-bold text-dark text-truncate"
                              style={{ fontSize: "0.9rem", maxWidth: "280px" }}
                            >
                              {item.drop}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* --- 4. CAR & FARE SECTION --- */}
                    <div className="px-3">
                      <div
                        className="d-flex justify-content-between align-items-center py-3"
                        style={{ borderTop: "1px dashed #e2e8f0" }}
                      >
                        <div
                          className="fw-bold text-dark d-flex align-items-center gap-2"
                          style={{ fontSize: "0.95rem" }}
                        >
                          <FaCar size={16} style={{ color: "#94a3b8" }} />{" "}
                          {item.carName}
                        </div>
                        <div className="text-end flex-shrink-0">
                          <span
                            className="d-block fw-bold text-muted mb-1"
                            style={{ fontSize: "0.6rem", letterSpacing: "1px" }}
                          >
                            TOTAL FARE
                          </span>
                          <div
                            className="fw-bold text-dark"
                            style={{ fontSize: "1.25rem" }}
                          >
                            ₹{item.price.toLocaleString()}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* --- 5. CARD FOOTER ACTIONS --- */}
                    <div
                      className="p-3 d-flex gap-2 bg-light border-top"
                      style={{ borderColor: "#f1f5f9" }}
                    >
                      {(item.status === "Confirmed" ||
                        item.status === "Pending") && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/enquiry/${item.enqRefNo}`);
                          }}
                          className="btn flex-fill rounded-pill fw-bold py-2 native-press shadow-sm"
                          style={{
                            fontSize: "0.9rem",
                            backgroundColor: "#fff7ed",
                            color: "#ea580c",
                            border: "1px solid #fed7aa",
                          }}
                        >
                          View Enquiry
                        </button>
                      )}

                      {item.status === "CancelPending" && (
                        <div
                          className="flex-fill d-flex align-items-center justify-content-center gap-2 rounded-pill py-2 shadow-sm"
                          style={{
                            background: "#fff1f2",
                            border: "1px solid #fecdd3",
                            fontSize: "0.85rem",
                            fontWeight: 700,
                            color: "#e11d48",
                          }}
                        >
                          ⏳ Cancel on Review
                        </div>
                      )}

                      {(item.status === "Completed" ||
                        item.status === "Cancelled" ||
                        item.status === "Expired" ||
                        item.status === "Revised") && (
                        <>
                          {item.status === "Completed" && (
                            <>
                              <button
                                className="btn flex-fill rounded-pill fw-bold py-2 native-press shadow-sm d-flex align-items-center justify-content-center gap-2"
                                style={{
                                  fontSize: "0.85rem",
                                  backgroundColor: "#ffffff",
                                  color: "#475569",
                                  border: "1px solid #e2e8f0",
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const base = Math.round(item.price * 0.85);
                                  const taxAndFees = Math.round(item.price * 0.05);
                                  const driverfee = Math.round(item.price * 0.1);
                                  navigate("/invoice", {
                                    state: {
                                      trip: {
                                        bookingId: item.referenceNo,
                                        date: item.date,
                                        pickup: item.pickup,
                                        drop: item.drop,
                                        totalFare: item.price,
                                        baseFare: base,
                                        taxes: taxAndFees,
                                        driver: driverfee,
                                        discount: 0,
                                      },
                                    },
                                  });
                                }}
                              >
                                <FaFileInvoice /> Invoice
                              </button>

                              {/* ✅ CONDITIONAL FEEDBACK UI */}
                              {item.feedbackRating ? (
                                // Show this if they ALREADY gave feedback
                                <div 
                                  className="flex-fill rounded-pill d-flex align-items-center justify-content-center gap-2 border shadow-sm"
                                  style={{ backgroundColor: "#f8fafc", borderColor: "#e2e8f0", padding: "0.4rem" }}
                                >
                                  <div className="d-flex text-warning gap-1">
                                    {[...Array(item.feedbackRating)].map((_, i) => (
                                      <FaStar key={i} size={12} />
                                    ))}
                                  </div>
                                  <span style={{ 
                                    color: item.feedbackRecommend === 1 ? "#166534" : "#991b1b",
                                    fontSize: "0.85rem" 
                                  }}>
                                    {item.feedbackRecommend === 1 ? <FaThumbsUp /> : <FaThumbsDown />}
                                  </span>
                                </div>
                              ) : (
                                // Show the Rate button if they HAVEN'T given feedback yet
                                <button
                                  className="btn flex-fill rounded-pill fw-bold py-2 native-press shadow-sm d-flex align-items-center justify-content-center gap-2"
                                  style={{
                                    fontSize: "0.85rem",
                                    backgroundColor: "#fff7ed",
                                    color: "#ea580c",
                                    border: "1px solid #fed7aa",
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate("/feedback", {
                                      state: {
                                        driverName: item.driver,
                                        driverId: item.driverId,
                                        carModel: item.carName,
                                        tripId: item.referenceNo,
                                        enqId: item.id,
                                      },
                                    });
                                  }}
                                >
                                  <FaStar /> Rate
                                </button>
                              )}
                            </>
                          )}
                          <button
                            className="btn flex-fill rounded-pill fw-bold py-2 native-press shadow-sm"
                            style={{
                              fontSize: "0.85rem",
                              background:
                                "linear-gradient(135deg, #f97316 0%, #ea580c 100%)",
                              color: "#ffffff",
                              border: "none",
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate("/book-ride");
                            }}
                          >
                            Book Again
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  {/* --- 6. TICKET CUT SEPARATOR (Shows between cards) --- */}
                  {index !== filteredBookings.length - 1 && (
                    <div
                      className="position-relative my-4 d-flex align-items-center animate-slide-up"
                      style={{ animationDelay: `${index * 0.05}s` }}
                    >
                      <div
                        className="position-absolute w-100"
                        style={{ borderTop: "2px dashed #cbd5e1" }}
                      ></div>
                      <div
                        className="position-relative z-index-1 d-flex align-items-center"
                        style={{
                          background:
                            "#f8f9fa" /* Matches your page background color */,
                          paddingRight: "15px",
                          color: "#94a3b8",
                          marginLeft: "30px",
                        }}
                      >
                        <FaCut size={16} />
                      </div>
                    </div>
                  )}
                </React.Fragment>
              ))
            ) : (
              /* --- EMPTY STATE --- */
              <div
                className="text-center py-5 mt-4 rounded-4 bg-white border shadow-sm animate-fade-in"
                style={{ borderColor: "#f1f5f9" }}
              >
                <div
                  className="bg-light rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                  style={{ width: "70px", height: "70px", color: "#f97316" }}
                >
                  <FaHistory size={24} style={{ opacity: 0.5 }} />
                </div>
                <h6 className="fw-bold text-dark mb-1">No {activeTab} rides</h6>
                <p className="text-muted small px-4">
                  When you book a ride, it will show up here.
                </p>
                <button
                  className="btn rounded-pill px-4 mt-2 fw-bold shadow-sm native-press"
                  onClick={() => navigate("/book-ride")}
                  style={{
                    background:
                      "linear-gradient(135deg, #f97316 0%, #ea580c 100%)",
                    color: "#ffffff",
                    border: "none",
                  }}
                >
                  Book a Ride
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Cancel Modal */}
      {showCancelModal && (
        <div
          className="modal-backdrop-custom"
          onClick={() => setShowCancelModal(false)}
        >
          <div
            className="modal-content-custom d-flex flex-column"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-100 d-flex justify-content-center pt-3 pb-2">
              <div
                className="bg-secondary rounded-pill"
                style={{ width: "40px", height: "5px", opacity: 0.3 }}
              ></div>
            </div>
            <div className="p-4 pt-2 text-center">
              <div className="bg-danger bg-opacity-10 rounded-circle d-inline-flex p-3 mb-3 border border-danger border-opacity-25">
                <FaTimes className="text-danger" size={28} />
              </div>
              <h5 className="fw-bold text-dark mb-2">Cancel this ride?</h5>
              <p className="text-muted small mb-4 px-2">
                Are you sure you want to cancel booking{" "}
                <b className="text-dark">{selectedBooking?.referenceNo}</b>?{" "}
                Cancellation policies may apply.
              </p>
              <div className="d-flex flex-column gap-2">
                <button
                  onClick={confirmCancellation}
                  disabled={cancelling}
                  className="btn btn-danger w-100 fw-bold py-3 rounded-pill btn-native-press shadow-sm"
                >
                  {cancelling ? "Cancelling..." : "Yes, Cancel Ride"}
                </button>
                <button
                  onClick={() => setShowCancelModal(false)}
                  disabled={cancelling}
                  className="btn btn-light text-dark w-100 fw-bold py-3 rounded-pill border border-light btn-native-press"
                >
                  Keep Ride
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .transition-all { transition: all 0.2s ease; }
        .z-index-1000 { z-index: 1000; }
        .z-index-1 { z-index: 1; }
        .tracking-tight { letter-spacing: -0.5px; }
        .btn-native-press { transition: transform 0.1s ease, box-shadow 0.1s ease; }
        .btn-native-press:active { transform: scale(0.97); box-shadow: none !important; opacity: 0.9; }
        .timeline-connect-line {
            width: 2px;
            background-image: linear-gradient(to bottom, #ced4da 50%, transparent 50%);
            background-size: 2px 8px;
            background-repeat: repeat-y;
            z-index: 0;
        }
        .border-dashed { border-top: 1px dashed #dee2e6 !important; }
        .feedback-btn-anim { transition: transform 0.15s ease-out; }
        .feedback-btn-anim.pop-out { transform: scale(1.1); opacity: 0; }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        .animate-fade-in { animation: fadeIn 0.3s ease-out forwards; }
        @keyframes slideUpFade {
            from { transform: translateY(20px); opacity: 0; }
            to   { transform: translateY(0); opacity: 1; }
        }
        .animate-slide-up { opacity: 0; animation: slideUpFade 0.4s ease-out forwards; }
        .modal-backdrop-custom {
            position: fixed; top: 0; left: 0; right: 0; bottom: 0;
            background: rgba(0,0,0,0.5); z-index: 1050;
            display: flex; justify-content: center; align-items: flex-end;
            animation: fadeIn 0.2s ease-out;
        }
        .modal-content-custom {
            background: white; width: 100%; max-width: 600px;
            border-radius: 24px 24px 0 0;
            padding-bottom: max(env(safe-area-inset-bottom), 16px);
            animation: slideUp 0.3s cubic-bezier(0.175, 0.885, 0.32, 1);
        }
        @keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
        @media (min-width: 601px) {
            .modal-backdrop-custom { align-items: center; }
            .modal-content-custom { border-radius: 20px; padding-bottom: 0; }
        }
      `}</style>
    </div>
  );
}